// @flow

import { isMeetingSettingEnabled } from '../jwt/functions';
import { PARTICIPANT_ROLE } from '../participants/constants';
import { getLocalParticipant } from '../participants/functions';
import { toState } from '../redux';

import { PERMISSIONS, PERMISSION_LIST, ROLE_PERMISSIONS, VISITOR_ROLE } from './constants';

/**
 * The meeting setting that takes screen sharing away from everyone who is not
 * a moderator (the toolbox reads the same key).
 */
const DISABLE_SCREEN_SHARING_FOR_GUEST = 'DISABLE_SCREEN_SHARING_FOR_GUEST';

/**
 * Resolves the effective role of the local participant.
 *
 * Being a visitor is tracked separately from the MUC role, and it always wins:
 * a visitor is view-only no matter what role their presence claims.
 *
 * The visitor flag is read straight off the store rather than through
 * visitors/functions' iAmVisitor: base/ must not import from a feature, or the
 * base <-> feature import cycle takes the whole bundle down at module-eval time.
 *
 * @param {Object|Function} stateful - Object or function that can be resolved to
 * the Redux state.
 * @returns {string} One of PARTICIPANT_ROLE or VISITOR_ROLE.
 */
export function getLocalRole(stateful: Object | Function) {
    const state = toState(stateful);

    if (state['features/visitors']?.iAmVisitor) {
        return VISITOR_ROLE;
    }

    return getLocalParticipant(state)?.role;
}

/**
 * Returns the capabilities granted to a role.
 *
 * @param {string} role - A PARTICIPANT_ROLE value or VISITOR_ROLE.
 * @returns {Array<string>} The permissions held by that role.
 */
export function getPermissionsForRole(role: string) {
    return ROLE_PERMISSIONS[role] ?? [];
}

/**
 * Whether the local participant holds a capability. Prefer this over checking the
 * role directly, so that a role change does not mean revisiting every feature.
 *
 * @param {Object|Function} stateful - Object or function that can be resolved to
 * the Redux state.
 * @param {string} permission - The PERMISSIONS value to check.
 * @returns {boolean}
 */
export function hasPermission(stateful: Object | Function, permission: string) {
    return getPermissionsForRole(getLocalRole(stateful)).includes(permission);
}

/**
 * Whether a given participant holds a capability.
 *
 * Note this reads the participant's role only; it cannot tell whether a remote
 * participant is a visitor, since visitors of the main room are not in the local
 * participant list at all.
 *
 * @param {Object} participant - The participant object.
 * @param {string} permission - The PERMISSIONS value to check.
 * @returns {boolean}
 */
export function participantHasPermission(participant: Object, permission: string) {
    return getPermissionsForRole(participant?.role).includes(permission);
}

/**
 * Turns a set of granted capabilities into the full yes/no map the indicator
 * renders, one entry per capability in {@link PERMISSION_LIST}.
 *
 * @param {Set<string>} granted - The capabilities that survived.
 * @returns {Object<string, boolean>} Capability name to boolean.
 */
function _toPermissionMap(granted: Set<string>) {
    const result = {};

    for (const permission of PERMISSION_LIST) {
        result[permission] = granted.has(permission);
    }

    return result;
}

/**
 * The capabilities a participant can actually use right now: what their role
 * grants, minus what the meeting's live state takes away.
 *
 * The runtime restrictions read straight off the store rather than through the
 * owning features' selectors, for the same base <-> feature import-cycle
 * reason getLocalRole gives:
 *
 * - send-media: a presenter never has it; nobody but a moderator has it while
 *   "block everyone's mic and camera" is on, while both of their devices are
 *   force-locked (lockAudio / lockVideo), or while AV moderation withholds both
 *   without having approved them;
 * - share-screen: taken from everyone but moderators by the meeting setting
 *   DISABLE_SCREEN_SHARING_FOR_GUEST, as the toolbox does;
 * - recording: taken from everyone but moderators while the local-recording
 *   policy is off.
 *
 * @param {Object|Function} stateful - Object or function that can be resolved to
 * the Redux state.
 * @param {Object} participant - The participant object.
 * @returns {Object<string, boolean>} Capability name to boolean, for every capability.
 */
export function getEffectiveParticipantPermissions(stateful: Object | Function, participant: Object) {
    const state = toState(stateful);
    const role = participant?.local ? getLocalRole(state) : participant?.role;
    const granted = new Set(getPermissionsForRole(role));

    if (role !== PARTICIPANT_ROLE.MODERATOR) {
        const id = participant?.id;
        const { lockAudio, lockVideo } = state['features/base/conference'] || {};
        const avModeration = state['features/av-moderation'] || {};
        const webinar = state['features/webinar'] || {};
        const audioLocked = Boolean(id && lockAudio?.[id]);
        const videoLocked = Boolean(id && lockVideo?.[id]);
        const audioWithheld = Boolean(avModeration.audioModerationEnabled)
            && !(id && avModeration.audioWhitelist?.[id]);
        const videoWithheld = Boolean(avModeration.videoModerationEnabled)
            && !(id && avModeration.videoWhitelist?.[id]);

        if (role === PARTICIPANT_ROLE.PRESENTER
                || webinar.mediaBlockAll === true
                || (audioLocked && videoLocked)
                || (audioWithheld && videoWithheld)) {
            granted.delete(PERMISSIONS.SEND_MEDIA);
        }

        if (isMeetingSettingEnabled(state, DISABLE_SCREEN_SHARING_FOR_GUEST)) {
            granted.delete(PERMISSIONS.SHARE_SCREEN);
        }

        if (webinar.localRecordingAllowed === false) {
            granted.delete(PERMISSIONS.RECORDING);
        }
    }

    return _toPermissionMap(granted);
}

/**
 * The same map for a person the local store does not know: a server roster row
 * (someone backstage, someone in another breakout room). Only the role is
 * known, so only the role matrix applies; the row's own flags cannot express
 * the runtime restrictions above.
 *
 * @param {string} role - The role the server reported: moderator, panelist,
 * presenter or participant.
 * @returns {Object<string, boolean>} Capability name to boolean, for every capability.
 */
export function getEffectiveRosterPermissions(role: ?string) {
    return _toPermissionMap(new Set(getPermissionsForRole(role || PARTICIPANT_ROLE.PARTICIPANT)));
}
